import { useQuery } from '@tanstack/react-query'
import { differenceInCalendarDays, formatDistance } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import { BsBoxArrowInDown, BsBoxArrowUp } from 'react-icons/bs'
import { MdOutlineImageNotSupported } from 'react-icons/md'
import { SiGooglemaps } from 'react-icons/si'
import { useParams, useSearchParams } from 'react-router-dom'

import { Avatar } from '../../components/Avatar'
import { TextArea } from '../../components/TextArea'
import { productProblemsMap } from '../../interfaces/productProblemsEnum'
import { cn } from '../../libs/tailwindMerge'
import {
  getReport,
  Report,
} from '../../services/reportServices'
import { formatDateRelativeTime } from '../../utils/formatDateRelativeTime'

export function GuestReportDetails() {
  const [loadedReport, setLoadedReport] = useState<Report | undefined>()
  const [selectedImage, setSelectedImage] = useState<Report['report_images'][number] | null>(null)

  const [isImageModalOpen, setIsImageModalOpen] = useState(false)

  const { id } = useParams()
  const [searchParams] = useSearchParams()

  const guestToken = searchParams.get('guestTokenAccess')

  const { data: report } = useQuery({
    queryKey: ['report_detail', id],
    queryFn: async () => {
      if (id) {
        return await getReport(id, guestToken)
      }
      return {
        id: '',
        user: {} as Report['user'],
        general_observations: '',
        improvement_suggestions: '',
        manager_observations: '',
        promoter_notes: '',
        report_images: [],
        report_product_problem: [],
        updated_at: new Date(),
        has_promoter_problem: false,
        store: {} as Report['store'],
        trade_marketing_check_date: new Date(),
      }
    },
  })

  function handleZoomImage(image: Report['report_images'][number]) {
    setSelectedImage(image)
    setIsImageModalOpen(true)
  }

  function handleCloseImageModal() {
    setSelectedImage(null)
    setIsImageModalOpen(false)
  }

  useEffect(() => {
    setLoadedReport(report)
  }, [report])

  if (!report || !loadedReport) {
    return (
      <div className="flex flex-col mx-auto max-w-screen-lg">
        Loading...
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="flex flex-col mx-auto max-w-screen-lg pb-16">
        <header className="px-2 mt-4">
          <div className="flex mt-8 items-center">
            <img
              src={loadedReport.store.group.logo_url}
              alt={loadedReport.store.group.name}
              className="w-12 h-12 rounded-full object-contain border-2 mr-2"
            />
            <div className="flex flex-col">
              <strong className="text-2xl">{loadedReport.store.name}</strong>
              <span className="font-semibold text-gray-600">
                {loadedReport.store.city} - {loadedReport.store.state}
              </span>
            </div>
          </div>
        </header>
        <main className="px-2 mt-8">
          <strong className="text-xl border-b pb-1 pr-4 border-gray-400">Problemas identificados</strong>
          <section className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <strong className="text-lg pl-2 text-gray-800 font-semibold">Produtos</strong>
            </div>
            {loadedReport.report_product_problem.length > 0
              ? (
                <div className="overflow-x-auto mt-4 md:mt-2 w-full border border-slate-500 rounded-lg">
                  <table className="flex-1 w-full text-sm text-left rounded-t-md overflow-hidden">
                    <thead className="text-xs uppercase bg-slate-50 bg-opacity-80">
                      <tr>
                        <th className="p-2 border-r border-slate-500">Produto - Cód.</th>
                        <th className="p-2 border-r border-slate-500 text-center">Data da criação</th>
                        <th className="p-2 border-r border-slate-500 text-center">Tipo</th>
                        <th className="p-2 border-r border-slate-500 text-center">Data</th>
                        <th className="p-2 text-right">Qtd.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadedReport.report_product_problem.map(item => (
                        <tr key={item.id} className="border-t border-slate-500 hover:bg-white_transparency hover:bg-opacity-15">
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap border-r border-slate-500"
                          >
                            {`${item.product.name}${item.product.code && ` - ${item.product.code}`}`}
                          </td>
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap border-r border-slate-500 text-center"
                          >
                            {
                              new Intl.DateTimeFormat('pt-BR', {
                                timeZone: 'UTC',
                              })
                                .format(
                                  new Date(item.created_at),
                                )
                            }
                          </td>
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap border-r border-slate-500 text-center"
                          >
                            {productProblemsMap[item.problem]}
                          </td>
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap border-r border-slate-500 text-center"
                          >
                            {item.expire_date
                              ? (
                                <>
                                  {
                                    new Intl.DateTimeFormat('pt-BR', {
                                      timeZone: 'UTC',
                                    })
                                      .format(
                                        new Date(item.expire_date),
                                      )
                                  }
                                </>
                                )
                              : (
                                <span>---</span>
                                )}
                          </td>
                          <td
                            scope="row"
                            className="p-1 pr-2 font-medium whitespace-nowrap text-right"
                          >
                            {item.qty || '---'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )
              : (
                <div className="flex items-center justify-center border-2 rounded-lg border-gray-300 border-dashed p-4">
                  <span>Nenhum</span>
                </div>
                )}
          </section>
          <section className="mt-6">
            <div className="flex items-center justify-between">
              <strong className="text-lg pl-2 text-gray-800 font-semibold">Promoção dos itens</strong>
            </div>
            {loadedReport.report_images.length === 0
              ? (
                <div className="flex w-full p-4 items-center justify-center border-2 rounded-md mt-4">
                  <span className="flex flex-col items-center font-semibold text-gray-500">
                    <MdOutlineImageNotSupported size={55} />
                    Nenhuma imagem
                  </span>
                </div>
                )
              : (
                <div className="max-h-[37.5rem] overflow-y-auto mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {loadedReport.report_images.map(image => (
                      <div
                        key={image.id}
                        className="overflow-hidden rounded-md relative"
                      >
                        <img
                          className="object-contain cursor-pointer"
                          src={image.url}
                          loading="lazy"
                          onClick={() => handleZoomImage(image)}
                        />
                        <span className={cn(
                          'flex items-center justify-center text-white absolute h-8 w-6 border border-t-0 rounded-b-full top-0 right-2',
                          differenceInCalendarDays(new Date(), image.created_at) < 1 && 'border-green-400 bg-green-600/80',
                          differenceInCalendarDays(new Date(), image.created_at) === 1 && 'border-amber-400 bg-amber-600/80',
                          differenceInCalendarDays(new Date(), image.created_at) > 1 && 'border-red-400 bg-red-600/80',
                        )}
                        >
                          {differenceInCalendarDays(new Date(), image.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                )}
            {loadedReport.promoters_staying_time && (
              <>
                <strong className="block mt-6">Registro de permanência em loja</strong>
                <main className="lg:px-0 flex-1 h-full">
                  <div
                    className="overflow-auto mt-4 md:mt-2 w-full max-h-[13rem] border border-slate-500 rounded-lg"
                  >
                    <table
                      className="flex-1 w-full text-sm text-left rounded-t-md overflow-hidden"
                    >
                      <thead className="text-xs uppercase bg-primary_700 bg-opacity-80">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 min-w-48 text-center border-r border-slate-500"
                          >
                            Promotor
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 border-r border-slate-500 text-center"
                          >
                            Dia
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 border-r border-slate-500 text-center"
                          >
                            Tempo
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 w-16 text-center"
                          >
                            Locais
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadedReport.promoters_staying_time.map(item => (
                          <tr
                            key={item.id}
                            className="border-t border-slate-500 hover:bg-white_transparency hover:bg-opacity-15"
                          >
                            <td
                              scope="row"
                              className="font-medium whitespace-nowrap sticky left-0 border-r border-slate-500 pl-2"
                            >
                              <div className="flex items-center">
                                <div className="rounded-md overflow-hidden p-2">
                                  <Avatar className="w-10 h-10 rounded-md border border-gray-300" imageUrl={item.promoter.avatar_url} />
                                </div>
                                <span>{item.promoter.name}</span>
                              </div>
                            </td>
                            <td
                              scope="row"
                              className="font-medium text-dark whitespace-nowrap text-center sticky left-0 border-r border-slate-500 p-2"
                            >
                              <span>
                                {
                                  new Intl.DateTimeFormat('pt-BR', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric',
                                  }).format(new Date(item.entry_time))
                                }
                              </span>
                            </td>
                            <td
                              scope="row"
                              className="font-medium text-dark whitespace-nowrap text-center sticky left-0 border-r border-slate-500 p-2"
                            >
                              {item.expired
                                ? (
                                  <span className="font-semibold">EXPIRADO</span>
                                  )
                                : (
                                  <>
                                    {item.leave_time
                                      ? (
                                        <span>
                                          {
                                        formatDistance(item.entry_time, item.leave_time, { locale: ptBR })
                                      }
                                        </span>
                                        )
                                      : (
                                        <span>Em andamento</span>
                                        )}
                                  </>
                                  )}
                            </td>
                            <td
                              scope="row"
                              className="font-medium text-dark whitespace-nowrap sticky p-2 text-center"
                            >
                              <div className="flex flex-row justify-center gap-2">
                                <a
                                  className="rounded-lg flex items-center justify-center p-3 bg-amber-600 hover:bg-amber-800 text-white transition-colors"
                                  href={`https://www.google.com/maps?q=${item.entry_time_coordinates.split(':')[0]},${item.entry_time_coordinates.split(':')[1]}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <SiGooglemaps />
                                  <BsBoxArrowInDown />
                                </a>
                                {item.leave_time_coordinates && (
                                  <a
                                    className="rounded-lg flex items-center justify-center p-3 bg-amber-600 hover:bg-amber-800 text-white transition-colors"
                                    href={`https://www.google.com/maps?q=${item.leave_time_coordinates.split(':')[0]},${item.leave_time_coordinates.split(':')[1]}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <SiGooglemaps />
                                    <BsBoxArrowUp />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </main>
              </>
            )}
          </section>
          <div className="mt-4">
            <section className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <TextArea
                  label="Observações, anotações, etc..."
                  defaultValue={loadedReport.promoter_notes}
                  readOnly
                />
              </div>
            </section>
          </div>
          <form className="mt-4">
            <div className="flex items-center justify-between">
              <strong className="text-xl border-b pb-1 pr-4 border-gray-400">Observações</strong>
            </div>
            <section className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <TextArea
                  label="Observações do gerente"
                  defaultValue={loadedReport.manager_observations}
                  readOnly
                />
              </div>
              <div>
                <TextArea
                  label="Sugestões de melhorias"
                  defaultValue={loadedReport.improvement_suggestions}
                  readOnly
                />
              </div>
              <div>
                <TextArea
                  label="Observações gerais"
                  defaultValue={loadedReport.general_observations}
                  readOnly
                />
              </div>
            </section>
          </form>
        </main>
      </div>
      {isImageModalOpen && selectedImage && (
        <div
          className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
          onClick={() => handleCloseImageModal()}
        >
          <div
            className="flex flex-col bg-slate-100 max-w-[90vw] max-h-[90dvh] relative rounded-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={selectedImage.url} />
            <div className="absolute right-2 top-2">
              <a
                className="rounded-lg flex items-center justify-center p-3 bg-white/50 hover:bg-yellow-500/50 hover:text-white"
                href={`https://www.google.com/maps?q=${selectedImage.latitude},${selectedImage.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                <SiGooglemaps />
              </a>
            </div>
            <footer className="absolute w-full bottom-0 left-0 bg-black/60 p-4">
              <span className="text-white font-semibold">Foto cadastrada {formatDateRelativeTime(selectedImage.created_at)}</span>
            </footer>
          </div>
        </div>
      )}
    </div>
  )
}
